import { CanvasTexture } from "three";

// Sharper ground when zoomed in: NASA GIBS Blue Marble tiles (500 m at the
// finest level, public domain) stitched into two nested patches around the
// view centre, a fine one at the resolution the screen needs and a coarse
// one 4x larger around it. The shader (earth-lighting.js) doesn't show the
// tiles' colour directly: GIBS serves one month (August 2004), so it applies
// their fine structure (detail / its blur at the global map's resolution)
// to the seasonal colour.
//
// Tiles are the EPSG:4326 "500m" matrix: 512 px, level n spans 288 / 2^n
// degrees (level 7: 2.25 degrees, ~490 m per pixel).

export const TILE_PX = 512;
export const MAX_LEVEL = 7;
// Coarsest levels worth using: level 3 is ~2.5x the 4k global map.
const MIN_COARSE_LEVEL = 3;
const MIN_FINE_LEVEL = MIN_COARSE_LEVEL + 2;
// Tiles along each side of a patch (4 x 512 = 2048 px).
export const PATCH_TILES = 4;

export const tileDegrees = (level) => 288 / 2 ** level;
const tileUrl = (level, row, col) =>
  `https://gibs.earthdata.nasa.gov/wmts/epsg4326/best/BlueMarble_NextGeneration/default/500m/${level}/${row}/${col}.jpeg`;

// Fine patch level for `degPerPixel` of ground per screen pixel: the
// coarsest tiles at least that sharp (clamped to the usable levels), or
// null when the global map (`baseDegPerPixel`) is already sharp enough.
export function fineLevelFor(degPerPixel, baseDegPerPixel) {
  if (degPerPixel >= baseDegPerPixel) return null;
  let level = MIN_FINE_LEVEL;
  while (level < MAX_LEVEL && tileDegrees(level) / TILE_PX > degPerPixel) level++;
  return level;
}

// Tile window of a patch centred near (lat, lng): first column (may be
// negative or past the end; wrapped when fetching), first row, and the
// covered rectangle in degrees.
export function patchWindow(level, lat, lng, tiles = PATCH_TILES) {
  const span = tileDegrees(level);
  const rows = Math.round(180 / span);
  const col0 = Math.round((lng + 180) / span - tiles / 2);
  const row0 = Math.max(0, Math.min(rows - tiles, Math.round((90 - lat) / span - tiles / 2)));
  return {
    level,
    col0,
    row0,
    key: `${level}/${row0}/${col0}`,
    rect: { lngMin: -180 + col0 * span, latMax: 90 - row0 * span, span: tiles * span },
  };
}

// Tile files (compressed, ~30-150 KB), shared by the patches; decoded when
// drawn. The least recently used are dropped.
function createTileCache(limit = 200) {
  const cache = new Map();
  const getBlob = (url) => {
    let entry = cache.get(url);
    if (entry) {
      cache.delete(url);
    } else {
      entry = fetch(url, { mode: "cors" }).then((r) => (r.ok ? r.blob() : Promise.reject(new Error(r.status))));
      entry.catch(() => cache.delete(url));
    }
    cache.set(url, entry);
    while (cache.size > limit) cache.delete(cache.keys().next().value);
    return entry;
  };
  return (url) => getBlob(url).then((blob) => createImageBitmap(blob));
}

// One patch: build(window) stitches its tiles and resolves to a texture
// (or null if superseded by a later build). Missing tiles stay mid grey,
// which the shader's ratio treats as "no detail".
function createPatch(getTile, prepare) {
  let generation = 0;
  return async (win, tiles = PATCH_TILES) => {
    const mine = ++generation;
    const current = () => mine === generation;
    const span = tileDegrees(win.level);
    const cols = Math.round(360 / span);
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = tiles * TILE_PX;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "rgb(128,128,128)";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const draws = [];
    for (let r = 0; r < tiles; r++) {
      for (let c = 0; c < tiles; c++) {
        const col = (((win.col0 + c) % cols) + cols) % cols;
        draws.push(
          getTile(tileUrl(win.level, win.row0 + r, col))
            .then((bitmap) => {
              if (current()) ctx.drawImage(bitmap, c * TILE_PX, r * TILE_PX);
              bitmap.close?.();
            })
            .catch(() => {})
        );
      }
    }
    await Promise.all(draws);
    if (!current()) return null;
    const texture = new CanvasTexture(canvas);
    prepare(texture);
    return texture;
  };
}

// The fine and coarse patches for a view. update(lat, lng, degPerPixel,
// baseDegPerPixel)
// rebuilds whichever changed and calls onChange(index, texture, rect) with
// index 0 = coarse, 1 = fine (texture null = none).
export function createDetailPatches({ prepare = () => {}, onChange }) {
  const getTile = createTileCache();
  const builders = [createPatch(getTile, prepare), createPatch(getTile, prepare)];
  const current = [null, null];
  return {
    update(lat, lng, degPerPixel, baseDegPerPixel) {
      const fine = fineLevelFor(degPerPixel, baseDegPerPixel);
      const wins = fine == null ? [null, null] : [patchWindow(fine - 2, lat, lng), patchWindow(fine, lat, lng)];
      wins.forEach((win, i) => {
        if ((win && win.key) === (current[i] && current[i].key)) return;
        current[i] = win;
        if (!win) {
          onChange(i, null, null);
          return;
        }
        builders[i](win).then((texture) => {
          if (texture && current[i] === win) onChange(i, texture, win.rect);
          else texture?.dispose();
        });
      });
    },
  };
}
